// Menu de transfert rapide (WKF-013 §7.4) — retour PO 2026-07-08 : cohérence avec
// le menu de report. Choisir un collègue = transfert IMMÉDIAT (comme un preset de
// report) ; « Plus d'options » ouvre la modale complète (motif, notification).
//
// Deux usages :
//   • <QuickTransferMenu> — DropdownMenu autonome (bouton Transférer du widget).
//   • <QuickTransferItems> — fragment d'items (DropdownMenuSub d'un kebab, focus…).
//
// Liée à un appel d'offres, la tâche ne part qu'à qui VOIT l'AO (décision PO n° 66) : le
// serveur refuse les autres en 422 TASK_ASSIGNEE_OUT_OF_TENDER, le menu ne les propose pas.

import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Forward } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { notificationErrorMessage, useNotificationMutations } from '@/hooks/useNotifications'
import type { Notification } from '@/hooks/useNotifications.types'

import { ColleagueAvatar } from './ColleagueAvatar'
import { lienDeLaNotification } from './lienDeLaNotification'
import { useDestinatairesDeLaTache } from './useDestinatairesDeLaTache'

/** Nombre de collègues montrés dans le sous-menu rapide (derniers utilisés d'abord). */
const QUICK_LIMIT = 5

interface QuickTransferSharedProps {
    notification: Notification
    entityId: string | null
    /** Ouvre la modale complète (motif + options de notification). */
    onMore: (n: Notification) => void
    /** Callback après un transfert réussi (ex. fermer un menu/modale parent, avancer). */
    onTransferred?: () => void
}

/** Items du menu (collègues + « Plus d'options »). À placer dans un content. */
export function QuickTransferItems({ notification, entityId, onMore, onTransferred }: QuickTransferSharedProps) {
    const { t } = useTranslation()
    const { ordered, remember, enCours } = useDestinatairesDeLaTache(
        entityId,
        lienDeLaNotification(notification),
    )
    const { transfer } = useNotificationMutations(entityId)
    // Derniers utilisés d'abord, plafonné (le reste via « Plus d'options »). Un clic transfère
    // AUSSITÔT : tant que la liste de l'AO lié est en route, aucun nom n'est offert.
    const top = enCours ? [] : ordered.slice(0, QUICK_LIMIT)

    const doTransfer = (recipientId: string) => {
        transfer.mutate(
            { id: notification.id, payload: { recipient_id: recipientId, notify_previous: true } },
            {
                onSuccess: () => {
                    remember(recipientId)
                    toast.success(t('notifications.transfer.done', 'Tâche transférée.'))
                },
                onError: (err) => toast.error(notificationErrorMessage(err, t)),
            },
        )
        onTransferred?.()
    }

    return (
        <>
            {top.map((c) => (
                <DropdownMenuItem key={c.id} onClick={() => doTransfer(c.id)} className="gap-2">
                    <ColleagueAvatar colleague={c} />
                    <span className="truncate">{c.full_name || c.email}</span>
                </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => onMore(notification)} className="gap-2">
                <Forward className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                {t('notifications.transfer.moreOptions', "Plus d'options")}
            </DropdownMenuItem>
        </>
    )
}

interface QuickTransferMenuProps extends QuickTransferSharedProps {
    align?: 'start' | 'end'
}

/** DropdownMenu autonome sur un bouton Forward (widget cloche). */
export function QuickTransferMenu({ align = 'end', ...shared }: QuickTransferMenuProps) {
    const { t } = useTranslation()
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground"
                    aria-label={t('notifications.actions.transfer', 'Transférer')}
                >
                    <Forward className="h-4 w-4" />
                </Button>
            </DropdownMenuTrigger>
            {/* data-nested-menu : garde anti-fermeture du panneau parent (widget). */}
            <DropdownMenuContent align={align} className="w-64" data-nested-menu="true">
                <QuickTransferItems {...shared} />
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
